import { useTranslation } from "react-i18next";

import { AnimalFigure } from "@/components/camp/AnimalFigure";
import type { AnimalId } from "@/components/camp/campData";

import { CornerAnimal, LANDING_CARD, LandingSection } from "./LandingSection";

/**
 * The first half of what the landing argues: what a call costs elsewhere, how
 * little it costs here, and who it is for (#942). Static prose over the
 * catalogue — a section here is a heading and a list.
 */

const COSTS = ["install", "password", "invite", "calendar", "check"] as const;
const QUOTES = ["club", "game", "mum"] as const;

export function LandingFriction() {
  const { t } = useTranslation();
  return (
    <LandingSection
      id="friction"
      eyebrow={t("landing.friction.eyebrow")}
      title={t("landing.friction.title")}
      lead={t("landing.friction.body")}
    >
      <div className="grid items-start gap-10 lg:grid-cols-2">
        <ul className="grid gap-2 text-muted-foreground">
          {QUOTES.map((quote) => (
            <li
              key={quote}
              className="border-l-2 border-primary/60 pl-3 text-base"
            >
              {t(`landing.friction.quote.${quote}`)}
            </li>
          ))}
        </ul>
        <div>
          <ol className="grid gap-2.5">
            {COSTS.map((cost, index) => (
              <li
                key={cost}
                className="flex items-center gap-3.5 rounded-2xl border border-border bg-card px-4 py-3.5 text-muted-foreground"
              >
                <span className="grid size-6 flex-none place-items-center rounded-full bg-stop/12 text-xs font-bold text-stop">
                  {index + 1}
                </span>
                <s className="decoration-stop decoration-2">
                  {t(`landing.friction.cost.${cost}`)}
                </s>
              </li>
            ))}
          </ol>
          <p className="mt-3.5 flex items-center gap-3.5 rounded-2xl border border-primary/40 bg-primary/10 p-4 shadow-glow-sm">
            <span className="grid size-7 flex-none place-items-center rounded-full bg-primary font-extrabold text-primary-foreground">
              1
            </span>
            <span>
              {t("landing.friction.instead")}{" "}
              <code className="rounded-lg bg-background/60 px-2 py-0.5 font-mono text-sm text-primary">
                {t("landing.room.code")}
              </code>
            </span>
          </p>
        </div>
      </div>
    </LandingSection>
  );
}

const PICKER: readonly AnimalId[] = [
  "fox",
  "hare",
  "owl",
  "bear",
  "raccoon",
  "frog",
];

function StepPicture({ step }: { step: "open" | "name" | "talk" }) {
  const { t } = useTranslation();
  const box =
    "mb-5 flex h-28 flex-col justify-center gap-2.5 rounded-2xl border border-border bg-background/60 p-4 text-sm";
  if (step === "open") {
    return (
      <div aria-hidden="true" className={box}>
        <span className="truncate rounded-xl bg-foreground/8 px-3 py-2 font-mono text-xs text-muted-foreground">
          {t("landing.how.open.address")}
          <b className="font-medium text-primary">{t("landing.room.code")}</b>
        </span>
        <span className="truncate px-3 font-mono text-xs text-muted-foreground/70">
          {t("landing.how.open.spoken")}
        </span>
      </div>
    );
  }
  if (step === "name") {
    return (
      <div aria-hidden="true" className={box}>
        <span className="rounded-xl bg-foreground/8 px-3 py-2">
          {t("landing.people.anna")}
        </span>
        <span className="flex gap-1.5">
          {PICKER.map((animal) => (
            <span
              key={animal}
              className={
                animal === "hare"
                  ? "grid size-8 place-items-end rounded-full bg-primary/20 ring-2 ring-primary"
                  : "grid size-8 place-items-end rounded-full bg-foreground/8"
              }
            >
              <AnimalFigure animal={animal} className="mx-auto !h-7 !w-5" />
            </span>
          ))}
        </span>
      </div>
    );
  }
  return (
    <div
      aria-hidden="true"
      className={`${box} flex-row items-center justify-center`}
    >
      {[0, 0.1, 0.25, 0.05, 0.3, 0.15, 0.2].map((delay) => (
        <i
          key={delay}
          className="landing-level w-1.5 rounded-full bg-primary"
          style={{ animationDelay: `${delay}s`, height: 22 }}
        />
      ))}
    </div>
  );
}

export function LandingHowItWorks() {
  const { t } = useTranslation();
  const steps = ["open", "name", "talk"] as const;
  return (
    <LandingSection
      id="how"
      eyebrow={t("landing.how.eyebrow")}
      title={t("landing.how.title")}
    >
      <ol className="grid gap-4 md:grid-cols-3">
        {steps.map((step, index) => (
          <li key={step} className={`${LANDING_CARD} relative`}>
            <span
              aria-hidden="true"
              className="absolute top-5 right-6 text-xl font-extrabold text-primary/15"
            >
              {index + 1}
            </span>
            <StepPicture step={step} />
            <h3 className="mb-2 text-lg font-semibold">
              {t(`landing.how.${step}.title`)}
            </h3>
            <p className="text-pretty text-muted-foreground">
              {t(`landing.how.${step}.body`)}
            </p>
          </li>
        ))}
      </ol>
    </LandingSection>
  );
}

const AUDIENCES: readonly { key: string; animal: AnimalId }[] = [
  { key: "tabletop", animal: "owl" },
  { key: "gaming", animal: "wolf" },
  { key: "everyday", animal: "hedge" },
];

export function LandingAudiences() {
  const { t } = useTranslation();
  return (
    <LandingSection
      id="who"
      eyebrow={t("landing.who.eyebrow")}
      title={t("landing.who.title")}
    >
      <ul className="grid gap-4 lg:grid-cols-3">
        {AUDIENCES.map(({ key, animal }) => (
          <li
            key={key}
            className={`${LANDING_CARD} relative flex flex-col gap-3 overflow-hidden`}
          >
            <CornerAnimal animal={animal} />
            <span className="text-xs font-semibold tracking-[0.06em] text-primary uppercase">
              {t(`landing.who.${key}.when`)}
            </span>
            <h3 className="text-lg font-semibold">
              {t(`landing.who.${key}.title`)}
            </h3>
            <p className="text-pretty text-muted-foreground">
              {t(`landing.who.${key}.body`)}
            </p>
            <p className="mt-auto rounded-2xl bg-background/60 px-4 py-3 text-sm">
              <span className="text-primary">— </span>
              {t(`landing.who.${key}.said`)}
            </p>
          </li>
        ))}
      </ul>
    </LandingSection>
  );
}
